import * as THREE from 'three';

export interface Emit {
  x: number; y: number; z: number;
  count?: number;
  /** Random offset around the origin. */
  spread?: number;
  vx?: number; vy?: number; vz?: number;
  /** Random velocity added on every axis. */
  vSpread?: number;
  colors: number[];
  size?: number; sizeSpread?: number;
  life?: number;
  gravity?: number;
  drag?: number;
  /** Size multiplier per second (smoke grows). */
  grow?: number;
  alpha?: number;
  /** Wander like a firefly or a falling leaf. */
  wander?: number;
}

/**
 * A pooled point-sprite particle system: one draw call per blend mode, CPU-updated.
 * Cheap enough to run chips, sparks, smoke, splashes and fireflies on phones.
 */
class Pool {
  readonly points: THREE.Points;
  private n = 0;
  private pos: Float32Array; private col: Float32Array; private size: Float32Array; private alpha: Float32Array;
  private vel: Float32Array; private life: Float32Array; private max: Float32Array; private base: Float32Array;
  private grav: Float32Array; private drag: Float32Array; private grow: Float32Array; private a0: Float32Array; private wander: Float32Array;
  private geo: THREE.BufferGeometry;
  scale = 1;

  constructor(readonly cap: number, additive: boolean, private uniforms: { uScale: { value: number } }) {
    this.pos = new Float32Array(cap * 3); this.col = new Float32Array(cap * 3); this.size = new Float32Array(cap); this.alpha = new Float32Array(cap);
    this.vel = new Float32Array(cap * 3); this.life = new Float32Array(cap); this.max = new Float32Array(cap); this.base = new Float32Array(cap);
    this.grav = new Float32Array(cap); this.drag = new Float32Array(cap); this.grow = new Float32Array(cap); this.a0 = new Float32Array(cap); this.wander = new Float32Array(cap);
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('color', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('size', new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('alpha', new THREE.BufferAttribute(this.alpha, 1).setUsage(THREE.DynamicDrawUsage));
    this.geo.setDrawRange(0, 0);
    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms, transparent: true, depthWrite: false, vertexColors: true,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      vertexShader: /* glsl */ `
        attribute float size; attribute float alpha;
        uniform float uScale;
        varying vec3 vC; varying float vA;
        void main() {
          vC = color; vA = alpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * uScale / max(-mv.z, 0.1);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vC; varying float vA;
        void main() {
          float d = length(gl_PointCoord - 0.5);
          if (d > 0.5) discard;
          gl_FragColor = vec4(vC, vA * smoothstep(0.5, 0.15, d));
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
    });
    this.points = new THREE.Points(this.geo, mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 2;
  }

  emit(e: Emit) {
    const c = new THREE.Color();
    const count = e.count ?? 1;
    for (let k = 0; k < count; k++) {
      if (this.n >= this.cap) this.kill(0);
      const i = this.n++, s = e.spread ?? 0, vs = e.vSpread ?? 0;
      this.pos[i * 3] = e.x + (Math.random() - 0.5) * s; this.pos[i * 3 + 1] = e.y + (Math.random() - 0.5) * s * 0.5; this.pos[i * 3 + 2] = e.z + (Math.random() - 0.5) * s;
      this.vel[i * 3] = (e.vx ?? 0) + (Math.random() - 0.5) * vs; this.vel[i * 3 + 1] = (e.vy ?? 0) + (Math.random() - 0.5) * vs; this.vel[i * 3 + 2] = (e.vz ?? 0) + (Math.random() - 0.5) * vs;
      c.set(e.colors[Math.floor(Math.random() * e.colors.length)]);
      this.col[i * 3] = c.r; this.col[i * 3 + 1] = c.g; this.col[i * 3 + 2] = c.b;
      this.base[i] = (e.size ?? 0.1) * (1 + (Math.random() - 0.5) * (e.sizeSpread ?? 0.4));
      this.size[i] = this.base[i];
      this.max[i] = this.life[i] = (e.life ?? 1) * (0.75 + Math.random() * 0.5);
      this.grav[i] = e.gravity ?? 0; this.drag[i] = e.drag ?? 0; this.grow[i] = e.grow ?? 0; this.a0[i] = e.alpha ?? 1; this.wander[i] = e.wander ?? 0;
      this.alpha[i] = 0;
    }
  }

  private kill(i: number) {
    const j = --this.n;
    if (i === j) return;
    for (const [arr, w] of [[this.pos, 3], [this.col, 3], [this.vel, 3]] as const) for (let k = 0; k < w; k++) arr[i * w + k] = arr[j * w + k];
    for (const arr of [this.size, this.alpha, this.life, this.max, this.base, this.grav, this.drag, this.grow, this.a0, this.wander]) arr[i] = arr[j];
  }

  update(dt: number, time: number) {
    for (let i = this.n - 1; i >= 0; i--) {
      this.life[i] -= dt;
      if (this.life[i] <= 0) { this.kill(i); continue; }
      const k = i * 3, d = Math.max(0, 1 - this.drag[i] * dt);
      this.vel[k + 1] -= this.grav[i] * dt;
      if (this.wander[i]) { this.vel[k] += Math.sin(time * 1.3 + i) * this.wander[i] * dt; this.vel[k + 2] += Math.cos(time * 1.1 + i * 1.7) * this.wander[i] * dt; }
      this.vel[k] *= d; this.vel[k + 1] *= d; this.vel[k + 2] *= d;
      this.pos[k] += this.vel[k] * dt; this.pos[k + 1] += this.vel[k + 1] * dt; this.pos[k + 2] += this.vel[k + 2] * dt;
      const age = 1 - this.life[i] / this.max[i];
      this.size[i] = this.base[i] * (1 + this.grow[i] * age * this.max[i]);
      this.alpha[i] = this.a0[i] * Math.min(1, age * 8) * Math.min(1, (this.life[i] / this.max[i]) * 2.5);
    }
    this.geo.setDrawRange(0, this.n);
    for (const name of ['position', 'color', 'size', 'alpha']) (this.geo.attributes[name] as THREE.BufferAttribute).needsUpdate = true;
  }
}

export class Particles {
  readonly uniforms = { uScale: { value: 400 } };
  readonly normal: Pool;
  readonly glow: Pool;
  budget = 1;
  constructor(scene: THREE.Scene) {
    this.normal = new Pool(900, false, this.uniforms);
    this.glow = new Pool(500, true, this.uniforms);
    scene.add(this.normal.points, this.glow.points);
  }
  resize(height: number, fovDeg: number) { this.uniforms.uScale.value = height / (2 * Math.tan((fovDeg * Math.PI) / 360)); }
  emit(e: Emit, additive = false) { const n = Math.max(1, Math.round((e.count ?? 1) * this.budget)); (additive ? this.glow : this.normal).emit({ ...e, count: n }); }
  update(dt: number, time: number) { this.normal.update(dt, time); this.glow.update(dt, time); }

  /* ---- recipes ---- */
  chips(x: number, y: number, z: number, colors = [0xd9b98a, 0x9b6b3a, 0xf0e2c4]) { this.emit({ x, y, z, count: 9, spread: 0.2, vy: 2.2, vSpread: 3, colors, size: 0.07, life: 0.7, gravity: 9, drag: 0.6 }); }
  shards(x: number, y: number, z: number, ore: number) {
    this.emit({ x, y, z, count: 8, spread: 0.2, vy: 2, vSpread: 3.2, colors: [0x8a857c, 0x6f6b64, ore], size: 0.07, life: 0.6, gravity: 10, drag: 0.5 });
    this.emit({ x, y, z, count: 4, vy: 1.5, vSpread: 4, colors: [0xffe6a0, 0xffffff], size: 0.05, life: 0.25, gravity: 6 }, true);
  }
  sparks(x: number, y: number, z: number) { this.emit({ x, y, z, count: 14, vy: 2.4, vSpread: 4.5, colors: [0xffc060, 0xff8a30, 0xfff0b0], size: 0.06, life: 0.5, gravity: 8, drag: 0.4 }, true); }
  splash(x: number, y: number, z: number, big = false) { this.emit({ x, y, z, count: big ? 16 : 6, spread: 0.3, vy: big ? 2.6 : 1.4, vSpread: big ? 2.4 : 1.2, colors: [0xffffff, 0xd8f4ff, 0x9fe0f0], size: 0.07, life: 0.6, gravity: 9, alpha: 0.9 }); }
  smoke(x: number, y: number, z: number, dark = false) { this.emit({ x, y, z, count: 1, spread: 0.15, vy: 0.55, vSpread: 0.2, vx: 0.18, colors: dark ? [0x5a5652, 0x6a6560] : [0xcfcac2, 0xb8b2aa], size: 0.35, life: 4, grow: 0.45, drag: 0.1, alpha: 0.32 }); }
  embers(x: number, y: number, z: number) { this.emit({ x, y, z, count: 1, spread: 0.3, vy: 1.1, vSpread: 0.5, colors: [0xffa040, 0xffd070], size: 0.05, life: 1.6, wander: 1.5, alpha: 0.9 }, true); }
  dust(x: number, y: number, z: number, color: number) { this.emit({ x, y, z, count: 3, spread: 0.25, vy: 0.4, vSpread: 0.6, colors: [color], size: 0.12, life: 0.7, grow: 0.8, drag: 1.5, alpha: 0.35 }); }
  sizzle(x: number, y: number, z: number) { this.emit({ x, y, z, count: 2, spread: 0.2, vy: 0.9, vSpread: 0.3, colors: [0xe8e4dc], size: 0.18, life: 1.4, grow: 0.6, alpha: 0.4 }); }
  leaves(x: number, y: number, z: number, colors: number[], count = 10) { this.emit({ x, y, z, count, spread: 1.6, vy: -0.25, vSpread: 0.5, colors, size: 0.09, life: 3.5, gravity: 0.15, drag: 0.8, wander: 1.4 }); }
  firefly(x: number, y: number, z: number) { this.emit({ x, y, z, count: 1, spread: 2, vy: 0.05, vSpread: 0.3, colors: [0xd8ff8a, 0xfff2a0], size: 0.08, life: 5, wander: 1.1, drag: 0.4 }, true); }
  /** A level-up firework: a few bursts of colour over someone's head, one after another. */
  fireworks(x: number, y: number, z: number, big = false) {
    const sets = [[0xffd870, 0xfff2c0], [0x7ad0ff, 0xe0f6ff], [0xff7a9a, 0xffe0e8], [0x9aff7a, 0xeaffe0], [0xc89aff, 0xf2e8ff]];
    const bursts = big ? 5 : 3;
    for (let b = 0; b < bursts; b++) setTimeout(() => {
      const cx = x + (Math.random() - 0.5) * 1.6, cy = y + 2.6 + Math.random() * 1.2, cz = z + (Math.random() - 0.5) * 1.6, col = sets[(b + Math.floor(Math.random() * 5)) % sets.length];
      for (let i = 0; i < 36; i++) {
        const u = Math.random() * 2 - 1, t = Math.random() * Math.PI * 2, r = Math.sqrt(1 - u * u), sp = 2.2 + Math.random() * 0.8;
        this.emit({ x: cx, y: cy, z: cz, count: 1, vx: r * Math.cos(t) * sp, vy: u * sp + 0.4, vz: r * Math.sin(t) * sp, colors: col, size: 0.08, life: 1.1 + Math.random() * 0.4, gravity: 1.6 }, true);
      }
    }, b * 280);
  }
  sparkle(x: number, y: number, z: number) {
    for (let i = 0; i < 24; i++) { const a = (i / 24) * Math.PI * 2; this.emit({ x: x + Math.cos(a) * 0.6, y: y + Math.random() * 0.6, z: z + Math.sin(a) * 0.6, count: 1, vx: Math.cos(a) * 0.6, vy: 1.8 + Math.random(), vz: Math.sin(a) * 0.6, colors: [0xffd870, 0xfff2c0], size: 0.09, life: 1.4, gravity: 0.8 }, true); }
  }
}
